/**
 * Express backend HTTP client.
 *
 * The only frontend code that talks to the Express API (`VITE_API_SERVER_URL`). Every
 * other module queries Supabase directly via `@/integrations/supabase/client`;
 * both are valid — Supabase-direct calls are bound by RLS, whereas Express
 * routes bypass RLS and enforce access themselves via `requireAuth`.
 *
 * Auth: attaches the caller's Supabase session JWT as a Bearer token. The
 * backend validates it and rejects with 401 when absent or expired.
 *
 * Envelope (backend/src/common/types/api-response.types.ts):
 *   success → { success: true,  message, data, meta? }
 *   failure → { success: false, message, error: { code, details? } }
 *
 * `apiGet` unwraps `data`; failures throw `ApiError` carrying the HTTP status,
 * the human `message`, and the stable `error.code` to branch on.
 *
 * GET /health is not enveloped — it is an infra probe and is not fetched here.
 */
import { supabase } from "@/integrations/supabase/client";
import { env } from "@/lib/env";

/** Fired once when the API rejects the session (401 / UNAUTHORIZED). AuthProvider listens. */
export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

/** Pagination envelope returned by list endpoints. */
export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** Optional stage-column sum (sales-pipeline deals list). */
  totalEstimatedValue?: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Stable backend code, e.g. "SCHOOL_NOT_FOUND". Absent for non-JSON failures. */
    readonly code?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Query params; `undefined` values are omitted. */
export type QueryParams = Record<
  string,
  string | number | boolean | undefined
>;

interface SuccessEnvelope<TData> {
  success: true;
  message: string;
  data: TData;
  meta?: PaginationMeta;
}

interface ErrorEnvelope {
  success: false;
  message: string;
  error: { code: string; details?: Record<string, unknown> };
}

type Envelope<TData> = SuccessEnvelope<TData> | ErrorEnvelope;

/** Prevents a burst of parallel 401s from toasting / redirecting repeatedly. */
let handlingSessionExpired = false;

function clearLocalAuthTokens(): void {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("sessionRoles");
}

function isOnAuthRoute(): boolean {
  const path = window.location.pathname;
  return (
    path.startsWith("/signin") ||
    path.startsWith("/login") ||
    path.startsWith("/auth") ||
    path.startsWith("/client/login") ||
    path.startsWith("/client/signup")
  );
}

/**
 * Clears stored tokens and notifies AuthProvider to toast + redirect.
 * Safe to call many times; only the first call in a session-expiry burst runs.
 */
function notifySessionExpired(): void {
  if (handlingSessionExpired) return;
  clearLocalAuthTokens();
  if (isOnAuthRoute()) return;
  handlingSessionExpired = true;
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
}

/** Call after a successful login so a later expiry can be handled again. */
export function resetSessionExpiredGuard(): void {
  handlingSessionExpired = false;
}

function isUnauthorizedResponse(
  status: number,
  body: Envelope<unknown> | null,
): boolean {
  if (status === 401) return true;
  return Boolean(body && body.success === false && body.error.code === "UNAUTHORIZED");
}

function buildUrl(path: string, params?: QueryParams): string {
  const base = env.API_URL.replace(/\/$/, "");
  const url = new URL(`${base}/api${path}`);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function authHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {};
  if (env.CLIENT_API_KEY) {
    headers["x-client-api-key"] = env.CLIENT_API_KEY;
  }

  /**
   * Prefer the Express login JWT (AuthContext → localStorage). Fall back to a
   * Supabase session token for flows that still authenticate via Supabase Auth.
   */
  const appToken = localStorage.getItem("accessToken");
  if (appToken) {
    headers.Authorization = `Bearer ${appToken}`;
    return headers;
  }

  const { data } = await supabase.auth.getSession();
  const supabaseToken = data.session?.access_token;
  if (supabaseToken) {
    headers.Authorization = `Bearer ${supabaseToken}`;
  }
  return headers;
}

/**
 * Performs the request and returns the parsed success envelope.
 * Throws {@link ApiError} on any non-2xx response or malformed body.
 */
async function request<TData>(
  path: string,
  options?: {
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    params?: QueryParams;
    body?: unknown;
  },
): Promise<SuccessEnvelope<TData>> {
  const method = options?.method ?? "GET";
  /* FormData sets its own multipart Content-Type (with the boundary); never override it. */
  const isForm = typeof FormData !== "undefined" && options?.body instanceof FormData;
  const headers: HeadersInit = {
    ...(await authHeaders()),
    ...(options?.body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
  };

  const response = await fetch(buildUrl(path, options?.params), {
    method,
    headers,
    body: isForm ? (options?.body as FormData) : options?.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  /** A crashed backend or a proxy can return HTML; don't let JSON.parse mask it. */
  let body: Envelope<TData> | null = null;
  try {
    body = (await response.json()) as Envelope<TData>;
  } catch {
    body = null;
  }

  if (!response.ok) {
    if (isUnauthorizedResponse(response.status, body)) {
      notifySessionExpired();
    }
    if (body && body.success === false) {
      throw new ApiError(response.status, body.message, body.error.code, body.error.details);
    }
    throw new ApiError(response.status, response.statusText);
  }

  if (!body || body.success !== true) {
    throw new ApiError(response.status, "Malformed response from the API");
  }
  return body;
}

/**
 * GETs a binary resource (a PDF) with the same auth as every other call.
 * Errors still arrive as the JSON envelope and throw {@link ApiError}.
 */
export async function apiGetBlob(path: string, params?: QueryParams): Promise<Blob> {
  const response = await fetch(buildUrl(path, params), { headers: await authHeaders() });
  if (!response.ok) {
    let body: Envelope<unknown> | null = null;
    try {
      body = (await response.json()) as Envelope<unknown>;
    } catch {
      body = null;
    }
    if (isUnauthorizedResponse(response.status, body)) notifySessionExpired();
    if (body && body.success === false) {
      throw new ApiError(response.status, body.message, body.error.code, body.error.details);
    }
    throw new ApiError(response.status, response.statusText);
  }
  return response.blob();
}

/** GETs `path` and returns the unwrapped `data` payload. */
export async function apiGet<TData>(path: string, params?: QueryParams): Promise<TData> {
  const body = await request<TData>(path, { params });
  return body.data;
}

/**
 * As {@link apiGet}, but also returns the pagination `meta`. Use for list
 * endpoints; single-object endpoints omit `meta` entirely.
 */
export async function apiGetWithMeta<TData>(
  path: string,
  params?: QueryParams,
): Promise<{ data: TData; meta: PaginationMeta | undefined }> {
  const body = await request<TData>(path, { params });
  return { data: body.data, meta: body.meta };
}

/** POSTs JSON `body` to `path` and returns the unwrapped `data` payload. */
export async function apiPost<TData>(path: string, body?: unknown): Promise<TData> {
  const envelope = await request<TData>(path, { method: "POST", body });
  return envelope.data;
}

/**
 * POSTs a multipart `form` (file uploads) to `path` and returns the unwrapped
 * `data` payload. Same auth, envelope and error handling as {@link apiPost}.
 */
export async function apiPostForm<TData>(path: string, form: FormData): Promise<TData> {
  const envelope = await request<TData>(path, { method: "POST", body: form });
  return envelope.data;
}

/** PUTs JSON `body` to `path` and returns the unwrapped `data` payload. */
export async function apiPut<TData>(path: string, body?: unknown): Promise<TData> {
  const envelope = await request<TData>(path, { method: "PUT", body });
  return envelope.data;
}

/** PATCHes JSON `body` to `path` and returns the unwrapped `data` payload. */
export async function apiPatch<TData>(path: string, body?: unknown): Promise<TData> {
  const envelope = await request<TData>(path, { method: "PATCH", body });
  return envelope.data;
}

/**
 * DELETEs `path` and returns the unwrapped `data` payload. Some endpoints
 * take a JSON body with the deletion (e.g. who revoked and why) — pass it as
 * `body`.
 */
export async function apiDelete<TData>(path: string, body?: unknown): Promise<TData> {
  const envelope = await request<TData>(path, { method: "DELETE", body });
  return envelope.data;
}

/**
 * As {@link apiPost}, but also returns pagination `meta` when present.
 */
export async function apiPostWithMeta<TData>(
  path: string,
  body?: unknown,
): Promise<{ data: TData; meta: PaginationMeta | undefined }> {
  const envelope = await request<TData>(path, { method: "POST", body });
  return { data: envelope.data, meta: envelope.meta };
}
